import React, { useEffect, useMemo, useState, Children, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/Badge';
import { RefreshButton } from '../shared/RefreshButton';
import { GoogleConnectCard } from '../shared/GoogleConnectCard';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { googleDrive, DriveFile, getDriveFileType, getDriveDownloadUrl, getDriveEmbedUrl } from '../../services/googleDrive';
import { HardDriveIcon, SearchIcon, ClockIcon, UsersIcon, StarIcon, FolderIcon, FileIcon, ExternalLinkIcon, AlertCircleIcon, XIcon, LinkIcon, DownloadIcon, LayoutGridIcon, ListIcon, ChevronRightIcon, MoreVerticalIcon } from 'lucide-react';
type TabType = 'drive' | 'recent' | 'shared' | 'starred';
type ViewMode = 'grid' | 'list';
interface FolderPathItem {
  id: string;
  name: string;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0
  }
};
function getInitials(name?: string) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';
}
function formatDate(dateStr: string | undefined, style: 'short' | 'long' = 'short'): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  if (style === 'short') {
    return d.toLocaleDateString('es-AR', {
      month: 'short',
      day: 'numeric'
    });
  }
  return d.toLocaleDateString('es-AR', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
export function GoogleDrivePanel() {
  const {
    isConnected,
    isLoading: authLoading,
    signIn
  } = useGoogleAuth();
  // State
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedError, setDismissedError] = useState(false);
  // Navigation State
  const [activeTab, setActiveTab] = useState<TabType>('drive');
  const [folderHistory, setFolderHistory] = useState<FolderPathItem[]>([{
    id: 'root',
    name: 'Mi Drive'
  }]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFile, setSelectedFile] = useState<DriveFile | null>(null);
  const currentFolderId = folderHistory[folderHistory.length - 1].id;
  const fetchFiles = async () => {
    if (!isConnected) return;
    setLoading(true);
    setError(null);
    setDismissedError(false);
    try {
      let data: DriveFile[] = [];
      if (searchQuery.trim()) {
        data = await googleDrive.searchFiles(searchQuery);
      } else if (activeTab === 'recent') {
        data = await googleDrive.getRecentFiles();
      } else if (activeTab === 'shared') {
        data = await googleDrive.getSharedFiles();
      } else if (activeTab === 'starred') {
        data = await googleDrive.getStarredFiles();
      } else {
        // activeTab === 'drive'
        data = await googleDrive.getFilesInFolder(currentFolderId);
      }
      // Sort: Folders first, then by name
      const sortedData = data.sort((a, b) => {
        const aIsFolder = a.mimeType === 'application/vnd.google-apps.folder';
        const bIsFolder = b.mimeType === 'application/vnd.google-apps.folder';
        if (aIsFolder && !bIsFolder) return -1;
        if (!aIsFolder && bIsFolder) return 1;
        return a.name.localeCompare(b.name);
      });
      setFiles(sortedData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar archivos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isConnected) {
      const timeoutId = setTimeout(() => {
        fetchFiles();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, activeTab, currentFolderId, searchQuery]);
  const handleFolderClick = (folder: DriveFile) => {
    setFolderHistory([...folderHistory, {
      id: folder.id,
      name: folder.name
    }]);
    setSearchQuery('');
  };
  const handleBreadcrumbClick = (index: number) => {
    setFolderHistory(folderHistory.slice(0, index + 1));
    setSearchQuery('');
  };
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchQuery('');
    if (tab === 'drive') {
      setFolderHistory([{
        id: 'root',
        name: 'Mi Drive'
      }]);
    }
  };
  const handleFileClick = (file: DriveFile) => {
    if (file.mimeType === 'application/vnd.google-apps.folder') {
      handleFolderClick(file);
    } else {
      const embedUrl = getDriveEmbedUrl(file);
      if (embedUrl) {
        setSelectedFile(file);
      } else if (file.webViewLink) {
        window.open(file.webViewLink, '_blank');
      }
    }
  };
  if (authLoading) {
    return <div className="h-full bg-[#020617] flex items-center justify-center" data-id="element-4762">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" data-id="element-4763" />
      </div>;
  }
  if (!isConnected) {
    return <GoogleConnectCard title="Google Drive" description="Conectá tu cuenta de Google para navegar por tus carpetas, buscar documentos y descargar archivos directamente desde el dashboard." accentColor="emerald" highlightService="drive" signIn={signIn} error={error} isLoading={authLoading} data-id="element-4764" />;
  }
  return <div className="h-full flex bg-[#020617] text-slate-200 overflow-hidden relative" data-id="element-4765">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/4 w-1/2 h-96 bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-4766" />

      {/* Sidebar (Drive Navigation) */}
      <div className="hidden md:flex flex-col w-64 border-r border-slate-800/60 bg-slate-900/30 backdrop-blur-md z-10" data-id="element-4767">
        <div className="p-6" data-id="element-4768">
          <div className="flex items-center gap-3 mb-8" data-id="element-4769">
            <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/10" data-id="element-4770">
              <HardDriveIcon size={20} className="text-emerald-400" data-id="element-4771" />
            </div>
            <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-400" data-id="element-4772">
              Drive
            </h1>
          </div>

          <nav className="space-y-1" data-id="element-4773">
            {[{
            id: 'drive',
            label: 'Mi Drive',
            icon: HardDriveIcon
          }, {
            id: 'recent',
            label: 'Recientes',
            icon: ClockIcon
          }, {
            id: 'shared',
            label: 'Compartidos',
            icon: UsersIcon
          }, {
            id: 'starred',
            label: 'Destacados',
            icon: StarIcon
          }].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => handleTabChange(tab.id as TabType)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? 'bg-emerald-500/10 text-emerald-400 shadow-sm border border-emerald-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent'}`} data-id="element-4774">
                  <Icon size={18} className={isActive ? 'text-emerald-400' : 'text-slate-500'} data-id="element-4775" />

                  {tab.label}
                </button>;
          })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 z-10" data-id="element-4776">
        {/* Top Header Bar */}
        <div className="h-16 border-b border-slate-800/60 bg-slate-900/40 backdrop-blur-md flex items-center justify-between px-4 lg:px-8 gap-4" data-id="element-4777">
          {/* Breadcrumbs or Title */}
          <div className="flex-1 min-w-0 flex items-center" data-id="element-4778">
            {searchQuery ? <h2 className="text-lg font-semibold text-slate-200 truncate" data-id="element-4779">
                Resultados de búsqueda
              </h2> : activeTab === 'drive' ? <div className="flex items-center text-lg font-semibold truncate" data-id="element-4780">
                {folderHistory.map((folder, index) => {
              const isLast = index === folderHistory.length - 1;
              return <Fragment key={folder.id} data-id="element-4781">
                      <button onClick={() => handleBreadcrumbClick(index)} className={`truncate transition-colors ${isLast ? 'text-slate-200 cursor-default' : 'text-slate-400 hover:text-emerald-400'}`} disabled={isLast} data-id="element-4782">
                        {folder.name}
                      </button>
                      {!isLast && <ChevronRightIcon size={16} className="mx-1 text-slate-600 shrink-0" data-id="element-4783" />}
                    </Fragment>;
            })}
              </div> : <h2 className="text-lg font-semibold text-slate-200 capitalize" data-id="element-4784">
                {activeTab === 'recent' ? 'Recientes' : activeTab === 'shared' ? 'Compartidos conmigo' : 'Destacados'}
              </h2>}
          </div>

          {/* Search & Actions */}
          <div className="flex items-center gap-3 shrink-0" data-id="element-4785">
            <div className="relative hidden sm:block w-64" data-id="element-4786">
              <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-4787" />

              <input type="text" placeholder="Buscar en Drive..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg py-1.5 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all" data-id="element-4788" />
            </div>

            <div className="flex items-center bg-slate-950/50 border border-slate-800 rounded-lg p-0.5" data-id="element-4789">
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-slate-800 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} title="Vista de grilla" data-id="element-4790">
                <LayoutGridIcon size={16} data-id="element-4791" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-slate-800 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} title="Vista de lista" data-id="element-4792">
                <ListIcon size={16} data-id="element-4793" />
              </button>
            </div>

            <RefreshButton onRefresh={fetchFiles} isRefreshing={loading} data-id="element-4794" />
          </div>
        </div>

        {/* Error Banner */}
        <AnimatePresence data-id="element-4795">
          {error && !dismissedError && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="px-4 lg:px-8 pt-4" data-id="element-4796">
              <div className="p-3 bg-red-950/40 border border-red-900/50 rounded-xl flex items-center gap-3 text-red-400" data-id="element-4797">
                <AlertCircleIcon size={18} className="shrink-0" data-id="element-4798" />
                <p className="text-sm flex-1" data-id="element-4799">{error}</p>
                <button onClick={() => setDismissedError(true)} className="p-1 hover:bg-red-900/50 rounded-md" data-id="element-4800">
                  <XIcon size={16} data-id="element-4801" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8" data-id="element-4802">
          {loading && files.length === 0 ? <div className="flex items-center justify-center h-64" data-id="element-4803">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" data-id="element-4804" />
            </div> : files.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-center border border-dashed border-slate-800/60 rounded-2xl bg-slate-900/20" data-id="element-4805">
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mb-4 border border-slate-800" data-id="element-4806">
                <FolderIcon size={24} className="text-slate-600" data-id="element-4807" />
              </div>
              <h3 className="text-lg font-medium text-slate-300 mb-1" data-id="element-4808">
                Carpeta vacía
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-4809">
                No hay archivos para mostrar aquí.
              </p>
            </div> : viewMode === 'grid' ? <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-4810">
              {files.map(file => {
            const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
            const typeInfo = getDriveFileType(file.mimeType);
            const downloadUrl = getDriveDownloadUrl(file);
            return <motion.div variants={itemVariants} key={file.id} onClick={() => handleFileClick(file)} className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4 hover:border-emerald-500/30 hover:bg-slate-800/50 transition-all cursor-pointer group relative flex flex-col h-36" data-id="element-4811">
                    {/* Hover Actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10" data-id="element-4812">
                      {downloadUrl && <a href={downloadUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md transition-colors" title="Descargar" data-id="element-4813">
                          <DownloadIcon size={14} data-id="element-4814" />
                        </a>}
                      <a href={file.webViewLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md transition-colors" title="Abrir en pestaña nueva" data-id="element-4815">
                        <ExternalLinkIcon size={14} data-id="element-4816" />
                      </a>
                    </div>

                    <div className="flex items-start gap-3 mb-3" data-id="element-4817">
                      <div className={`mt-0.5 ${isFolder ? 'text-slate-400' : typeInfo.color}`} data-id="element-4818">
                        {isFolder ? <FolderIcon size={24} className="fill-slate-800" data-id="element-4819" /> : <span className="text-2xl leading-none" data-id="element-4820">
                            {typeInfo.icon}
                          </span>}
                      </div>
                      <h3 className="text-sm font-medium text-slate-200 group-hover:text-emerald-400 transition-colors line-clamp-2 pr-12 leading-tight" data-id="element-4821">
                        {file.name}
                      </h3>
                    </div>

                    <div className="mt-auto flex items-center justify-between text-xs text-slate-500" data-id="element-4822">
                      <div className="flex items-center gap-1.5 truncate" data-id="element-4823">
                        {file.owners?.[0] && <>
                            <div className="w-4 h-4 rounded-full bg-slate-800 flex items-center justify-center text-[8px] font-bold text-slate-400 shrink-0" data-id="element-4824">
                              {getInitials(file.owners[0].displayName)}
                            </div>
                            <span className="truncate max-w-[80px]" data-id="element-4825">
                              {file.owners[0].displayName.split(' ')[0]}
                            </span>
                          </>}
                      </div>
                      <span className="shrink-0" data-id="element-4826">
                        {formatDate(file.modifiedTime)}
                      </span>
                    </div>
                  </motion.div>;
          })}
            </motion.div> /* List View */ : <div className="bg-slate-900/30 border border-slate-800/60 rounded-xl overflow-hidden" data-id="element-4827">
              <table className="w-full text-left border-collapse" data-id="element-4828">
                <thead data-id="element-4829">
                  <tr className="border-b border-slate-800/60 text-xs font-medium text-slate-500 bg-slate-900/50" data-id="element-4830">
                    <th className="py-3 px-4 font-medium" data-id="element-4831">Nombre</th>
                    <th className="py-3 px-4 font-medium hidden sm:table-cell" data-id="element-4832">
                      Propietario
                    </th>
                    <th className="py-3 px-4 font-medium hidden md:table-cell" data-id="element-4833">
                      Última modificación
                    </th>
                    <th className="py-3 px-4 font-medium text-right" data-id="element-4834">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-4835">
                  {files.map(file => {
                const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
                const typeInfo = getDriveFileType(file.mimeType);
                const downloadUrl = getDriveDownloadUrl(file);
                return <tr key={file.id} onClick={() => handleFileClick(file)} className="border-b border-slate-800/30 hover:bg-slate-800/40 transition-colors cursor-pointer group" data-id="element-4836">
                        <td className="py-3 px-4" data-id="element-4837">
                          <div className="flex items-center gap-3" data-id="element-4838">
                            <div className={isFolder ? 'text-slate-400' : typeInfo.color} data-id="element-4839">
                              {isFolder ? <FolderIcon size={18} className="fill-slate-800" data-id="element-4840" /> : <span className="text-lg leading-none" data-id="element-4841">
                                  {typeInfo.icon}
                                </span>}
                            </div>
                            <span className="text-sm font-medium text-slate-200 group-hover:text-emerald-400 transition-colors truncate max-w-[200px] sm:max-w-xs md:max-w-md" data-id="element-4842">
                              {file.name}
                            </span>
                            {file.starred && <StarIcon size={12} className="text-amber-400 fill-amber-400 shrink-0" data-id="element-4843" />}
                          </div>
                        </td>
                        <td className="py-3 px-4 hidden sm:table-cell text-sm text-slate-400 truncate max-w-[120px]" data-id="element-4844">
                          {file.owners?.[0]?.displayName || '—'}
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell text-sm text-slate-400" data-id="element-4845">
                          {formatDate(file.modifiedTime, 'long')}
                        </td>
                        <td className="py-3 px-4 text-right" data-id="element-4846">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4847">
                            {downloadUrl && <a href={downloadUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-md transition-colors" title="Descargar" data-id="element-4848">
                                <DownloadIcon size={16} data-id="element-4849" />
                              </a>}
                            <a href={file.webViewLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-md transition-colors" title="Abrir en pestaña nueva" data-id="element-4850">
                              <ExternalLinkIcon size={16} data-id="element-4851" />
                            </a>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </div>
      </div>

      {/* Preview Overlay */}
      <AnimatePresence data-id="element-4852">
        {selectedFile && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-12 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedFile(null)} data-id="element-4853">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="w-full h-full max-w-7xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-4854">
              {/* Overlay Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/50" data-id="element-4855">
                <div className="flex items-center gap-3 min-w-0" data-id="element-4856">
                  <div className="text-xl leading-none" data-id="element-4857">
                    {getDriveFileType(selectedFile.mimeType).icon}
                  </div>
                  <h2 className="text-sm font-bold text-slate-200 truncate pr-4" data-id="element-4858">
                    {selectedFile.name}
                  </h2>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-id="element-4859">
                  {getDriveDownloadUrl(selectedFile) && <a href={getDriveDownloadUrl(selectedFile)!} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-4860">
                      <DownloadIcon size={14} data-id="element-4861" />
                      <span className="hidden sm:inline" data-id="element-4862">Descargar</span>
                    </a>}
                  <a href={selectedFile.webViewLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-colors" data-id="element-4863">
                    <ExternalLinkIcon size={14} data-id="element-4864" />
                    <span className="hidden sm:inline" data-id="element-4865">Abrir en Google</span>
                  </a>
                  <div className="w-px h-4 bg-slate-700 mx-1" data-id="element-4866" />
                  <button onClick={() => setSelectedFile(null)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-4867">
                    <XIcon size={18} data-id="element-4868" />
                  </button>
                </div>
              </div>

              {/* Iframe Container */}
              <div className="flex-1 bg-white relative" data-id="element-4869">
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-slate-400" data-id="element-4870">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" data-id="element-4871" />
                  <p className="text-sm" data-id="element-4872">Cargando vista previa...</p>
                  <p className="text-xs text-slate-500 mt-2 max-w-sm text-center" data-id="element-4873">
                    Si el documento no carga, verificá que tengas sesión
                    iniciada en Google en este navegador o abrilo en una nueva
                    pestaña.
                  </p>
                </div>

                <iframe src={getDriveEmbedUrl(selectedFile) || ''} className="absolute inset-0 w-full h-full border-0 z-10 bg-white" title={selectedFile.name} allow="autoplay; camera; microphone; fullscreen; display-capture" sandbox="allow-same-origin allow-scripts allow-popups allow-forms" data-id="element-4874" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}